import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InstallLink {
  id: string;
  institutionUid: string;
  institutionName: string;
  modules: string[];
  caddyDomain: string;
  /** Prefijo DICOM / AET guardado al generar el enlace (para la descarga vía API sin llamar a Imaging). */
  prefixDicom?: string;
  used: boolean;
  createdBy: string;
  createdAt: any;
  /** Referencia a una API Key (existente o recién creada). */
  apiKeyId?: string;
  /** Nombre descriptivo de la API Key (solo lectura). */
  apiKeyName?: string;
}

export interface CreateInstallLinkData {
  institutionUid: string;
  institutionName: string;
  modules: string[];
  caddyDomain: string;
  createdBy: string;
  /** Opcional: se persiste para el mismo cuerpo del POST /installation/package en la página del enlace. */
  prefixDicom?: string;
  /** Referencia a una API Key (existente o recién creada). */
  apiKeyId?: string;
  /** Nombre descriptivo de la API Key para mostrar en el link público. */
  apiKeyName?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const INSTALL_LINKS_COLLECTION = "install_links";

/**
 * Returns the next sequential install number (e.g., 1 → "install-01", 12 → "install-12")
 */
export async function getNextInstallNumber(): Promise<string> {
  const coll = collection(db, INSTALL_LINKS_COLLECTION);
  const q = query(coll, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);

  if (snapshot.empty) return "install-01";

  // Find the highest number from existing docs
  let maxNum = 0;
  snapshot.forEach((docSnap) => {
    const id = docSnap.id; // e.g., "install-01"
    const match = id.match(/^install-(\d+)$/);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num > maxNum) maxNum = num;
    }
  });

  const next = maxNum + 1;
  return `install-${String(next).padStart(2, "0")}`;
}

/**
 * Create a new install link document in Firestore
 */
export async function createInstallLink(data: CreateInstallLinkData): Promise<string> {
  const id = await getNextInstallNumber();

  const institutionName =
    data.institutionName?.trim() || data.institutionUid || "Sin nombre";

  const docRef = doc(db, INSTALL_LINKS_COLLECTION, id);
  await setDoc(docRef, {
    id,
    institutionUid: data.institutionUid,
    institutionName,
    modules: data.modules,
    caddyDomain: data.caddyDomain,
    ...(data.prefixDicom?.trim() ? { prefixDicom: data.prefixDicom.trim() } : {}),
    ...(data.apiKeyId?.trim() ? { apiKeyId: data.apiKeyId.trim() } : {}),
    ...(data.apiKeyName?.trim() ? { apiKeyName: data.apiKeyName.trim() } : {}),
    used: false,
    createdBy: data.createdBy,
    createdAt: serverTimestamp(),
  });

  return id;
}

/**
 * Get an install link by ID. Returns null if not found.
 */
export async function getInstallLink(id: string): Promise<InstallLink | null> {
  const docRef = doc(db, INSTALL_LINKS_COLLECTION, id);
  const snap = await getDoc(docRef);

  if (!snap.exists()) return null;

  return { id, ...snap.data() } as InstallLink;
}

/**
 * Mark an install link as used (one-time access)
 */
export async function markInstallLinkUsed(id: string): Promise<void> {
  const docRef = doc(db, INSTALL_LINKS_COLLECTION, id);
  await updateDoc(docRef, { used: true });
}
